import React, { useEffect, useState } from "react";
import { usePermissions } from "../hooks/usePermissions";
import { useAuth } from "../contexts/AuthContext";
import TaskService from "../services/Task.service";
import AnnouncementService from "../services/Announcement.service";
import {
    Grid,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";
import kodexLogo from "../assets/kodex_logo.svg";
import {
    AccessTime as TimesheetsIcon,
    Receipt as ExpensesIcon,
    Assignment as TasksIcon,
    Folder as ProjectsIcon,
    Assessment as ReportsIcon,
    Campaign as AnnouncementsIcon,
    BeachAccess as HolidaysIcon,
    Engineering as FieldIcon,
    BuildCircle as ShopIcon,
    Architecture as EngineeringIcon,
    VideoLibrary as VideosIcon,
    CalendarMonth as ScheduleIcon,
    DirectionsCar as VehicleIcon,
    NoteAlt as WorkOrderIcon,
    PrecisionManufacturing as EquipmentIcon,
    ListAlt as BOMIcon,
    QrCodeScanner as ScanIcon,
    Search as MaterialSearchIcon,
    CreditCard as CompanyCardIcon,
    AccountBalanceWallet as ReimbursableIcon,
    Hotel as PerDiemIcon,
    MenuBook as HandbookIcon,
    Warning as IncidentReportIcon,
    FolderOpen as ProjectReportIcon,
    SensorDoor as DoorServiceReportIcon,
    SettingsInputAntenna as RFTestReportIcon,
    ListAlt as ECNIcon,
    Storage as CRMIcon,
} from "@mui/icons-material";
import { useNavigate, Navigate } from "react-router-dom";

const dashboardItems = [
    {
        title: "Timesheets",
        desc: "Log daily hours and submit for approval.",
        path: "/timesheets",
        icon: TimesheetsIcon,
        color: "primary",
    },
    {
        title: "Tasks",
        desc: "View and manage your assigned tasks.",
        path: "/tasks",
        icon: TasksIcon,
        color: "primary",
    },
    {
        title: "Projects",
        desc: "Manage active projects and timelines.",
        path: "/projects",
        icon: ProjectsIcon,
        color: "primary",
    },
    {
        title: "Expenses",
        desc: "Submit and track expense reports.",
        path: "/expenses",
        icon: ExpensesIcon,
        color: "secondary",
    },
    {
        title: "Reports",
        desc: "Generate and review operational reports.",
        path: "/reports",
        icon: ReportsIcon,
        color: "primary",
    },
    {
        title: "Announcements",
        desc: "Latest company news and updates.",
        path: "/announcements",
        icon: AnnouncementsIcon,
        color: "secondary",
    },
    {
        title: "Field",
        desc: "Field schedules and site reports.",
        path: "/field",
        icon: FieldIcon,
        color: "primary",
    },
    {
        title: "Shop",
        desc: "Work orders and production status.",
        path: "/shop",
        icon: ShopIcon,
        color: "primary",
    },
    {
        title: "Engineering",
        desc: "ECNs, drawings, and schedules.",
        path: "/engineering",
        icon: EngineeringIcon,
        color: "primary",
    },
    {
        title: "Holidays",
        desc: "Company holidays and PTO schedules.",
        path: "/holidays",
        icon: HolidaysIcon,
        color: "secondary",
    },
    {
        title: "Videos",
        desc: "Training videos and instructional content.",
        path: "/videos",
        icon: VideosIcon,
        color: "primary",
    },
    {
        title: "Company Handbook",
        desc: "Policies, procedures, and employee guidelines.",
        path: "/handbook",
        icon: HandbookIcon,
        color: "secondary",
    },
    {
        title: "CRM",
        desc: "Look up companies, contacts, projects, and quotes.",
        path: "/crm",
        icon: CRMIcon,
        color: "primary",
        roles: ["isAdmin", "isManagement"],
    },
];

function Dashboard() {
    const navigate = useNavigate();
    const permissions = usePermissions();
    const { isSubcontractor } = permissions;
    const { user } = useAuth();
    const visibleDashboardItems = dashboardItems.filter(
        (item) => !item.roles || item.roles.some((key) => permissions[key]),
    );
    const [openTaskCount, setOpenTaskCount] = useState(null);
    const [unreadAnnouncementCount, setUnreadAnnouncementCount] = useState(null);

    useEffect(() => {
        if (!user?.id) return;
        TaskService.list({ assignee: user.id, page_size: 1 })
            .then((res) => setOpenTaskCount(res.data.count ?? null))
            .catch(() => {});
        AnnouncementService.unreadCount()
            .then((res) => setUnreadAnnouncementCount(res.data.count ?? null))
            .catch(() => {});
    }, [user?.id]);

    useEffect(() => {
        const handler = () => setUnreadAnnouncementCount(0);
        window.addEventListener("announcementsRead", handler);
        return () => window.removeEventListener("announcementsRead", handler);
    }, []);

    if (isSubcontractor) {
        return <Navigate to="/subcontractor" replace />;
    }

    return (
        <Box>
            {/* Branding header */}
            <Box sx={{ mb: 3, textAlign: "center" }}>
                <img
                    src={kodexLogo}
                    alt="Kodex Shielding Solutions"
                    style={{
                        height: 100,
                        width: "auto",
                        marginBottom: 4,
                    }}
                />
                <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{
                        fontFamily: "Montserrat, sans-serif",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                    }}
                >
                    {isSubcontractor ? "Subcontractor Portal" : "Employee Portal"}
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {visibleDashboardItems.map(
                    ({ title, desc, path, icon: Icon, color }, index) => {
                        const badge =
                            title === "Tasks" ? openTaskCount :
                            title === "Announcements" ? unreadAnnouncementCount :
                            null;
                        // Give the trailing item a full-width row instead of leaving it stranded
                        // next to empty space when it's alone in the last row at a breakpoint.
                        const isLast = index === visibleDashboardItems.length - 1;
                        const spanFull = (cols) => isLast && visibleDashboardItems.length % cols === 1;
                        const xs = spanFull(2) ? 12 : 6;
                        const sm = spanFull(3) ? 12 : 4;
                        const md = spanFull(4) ? 12 : 3;
                        return (
                        <Grid item xs={xs} sm={sm} md={md} key={path}>
                            <Card sx={{ height: "100%", boxShadow: "none", border: 1, borderColor: "primary.main", display: "flex", flexDirection: "column" }}>
                                <CardActionArea
                                    onClick={() => navigate(path)}
                                    sx={{ flex: 1, alignItems: "flex-start" }}
                                >
                                    <CardContent>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 1,
                                            }}
                                        >
                                            <Icon color={color} fontSize="medium" />
                                            {badge != null && badge > 0 && (
                                                <Chip
                                                    label={badge}
                                                    size="small"
                                                    color="secondary"
                                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontFamily: "Montserrat, sans-serif",
                                                fontWeight: 700,
                                                lineHeight: 1.3,
                                                mb: 0.5,
                                            }}
                                        >
                                            {title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {desc}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 0.5,
                                        px: 1.5,
                                        pb: 1,
                                        minHeight: 34,
                                        overflowX: "auto",
                                        overflowY: "hidden",
                                        "&::-webkit-scrollbar": { display: "none" },
                                        scrollbarWidth: "none",
                                        "& > *": { flexShrink: 0 },
                                    }}
                                >
                                    {title === "Field" && (
                                        <>
                                            <Tooltip title="Schedule">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/field/schedule"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <ScheduleIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Vehicles">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/field/vehicles"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <VehicleIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    {title === "Expenses" && (
                                        <>
                                            <Tooltip title="Company Card">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/expenses/company-card"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <CompanyCardIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reimbursable">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/expenses/reimbursable"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <ReimbursableIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Miles">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/expenses/miles"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <VehicleIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Per Diem">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/expenses/per-diem"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <PerDiemIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    {title === "Reports" && (
                                        <>
                                            <Tooltip title="Incident Report">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/reports/incident"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <IncidentReportIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Project Report">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/reports/project"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <ProjectReportIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Door Service Report">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/reports/door-service"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <DoorServiceReportIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="RF Test Report">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/reports/rf-test"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <RFTestReportIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    {title === "Engineering" && (
                                        <>
                                            <Tooltip title="ECNs">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/engineering/ecns"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <ECNIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Engineering Schedule">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/engineering/schedule"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <ScheduleIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    {title === "Shop" && (
                                        <>
                                            <Tooltip title="Work Orders">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/shop/work-orders"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <WorkOrderIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Equipment">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/shop/equipment"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <EquipmentIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Bill of Materials">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/shop/bom"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <BOMIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Scan QR">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/scan"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <ScanIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Find Material">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={(e) => { e.stopPropagation(); navigate("/shop/material/search"); }}
                                                    sx={{ border: 1, borderColor: "primary.main" }}
                                                >
                                                    <MaterialSearchIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    );
                    }
                )}
            </Grid>
        </Box>
    );
}

export default Dashboard;
